import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Compass, CheckCircle2, ChevronRight, BarChart3, HelpCircle, Layers, ShieldCheck, Heart, User, Sparkles } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen pt-20 bg-[#F9F6F0] text-[#352520] relative">
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center py-20 px-6 md:px-12 overflow-hidden">
        {/* Animated backdrop glows */}
        <div className="bg-blob blob-gold top-[10%] left-[5%] w-[500px] h-[500px]" />
        <div className="bg-blob blob-saffron bottom-[15%] right-[5%] w-[600px] h-[600px]" />
        
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          {/* Left Text Column with Fade-in Animation */}
          <div className="flex flex-col items-start text-left max-w-xl opacity-0 animate-fade-in-up">
            {/* Elegant Badge */}
            <div className="inline-flex items-center gap-1.5 bg-[#FDE68A]/20 rounded-full px-4.5 py-1.5 mb-6 border border-[#EBE6DD]">
              <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
              <span className="text-[10px] font-bold tracking-widest uppercase text-[#D97706]">
                ✦ AI-Powered Vastu Architecture
              </span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-vedic text-[#352520] leading-[1.15] mb-6">
              Harmonize Your Space.
              <br />
              <span className="text-[#826239]">Elevate Your Spirit.</span>
            </h1>
            
            {/* Description Subtext */}
            <p className="text-[#5D4E37] text-base md:text-lg font-light leading-relaxed mb-8">
              Vastuspace.AI bridges millennia-old architectural wisdom with cutting-edge artificial intelligence to analyze, optimize, and sanctify your living and working environments.
            </p>
            
            {/* CTA Action Buttons */}
            <div className="flex flex-wrap items-center gap-6">
              <Link
                to="/upload"
                className="bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm px-8 py-3.5 rounded-full shadow-lg shadow-[#D97706]/20 transition-all hover:scale-105 active:scale-98 duration-300"
              >
                Analyze My Space
              </Link>
              <Link
                to="/upload"
                className="group flex items-center gap-1 text-[#826239] hover:text-[#352520] font-semibold text-sm transition-colors"
              >
                <span>View Demo</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
          
          {/* Right Yantra Visual Column with Animations */}
          <div className="relative flex items-center justify-center opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            {/* Soft gold rotating outer ring */}
            <div className="absolute w-[85%] h-[85%] rounded-full border border-[#D97706]/15 animate-slow-spin pointer-events-none" />
            
            {/* Central detailed Yantra image with floating and pulse gold effect */}
            <div className="relative w-full max-w-[450px] aspect-square rounded-full overflow-hidden border-2 border-[#EBE6DD] bg-white shadow-2xl p-4 animate-float animate-pulse-gold hover:scale-[1.03] transition-transform duration-500">
              <img
                src="/yantra.jpg"
                alt="Sacred Golden Yantra mandala"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
            
            {/* Floating Compass Badge (Far Top-Right) */}
            <div className="absolute top-[5%] right-[5%] w-12 h-12 rounded-full bg-white shadow-lg border border-[#EBE6DD] flex items-center justify-center text-[#D97706] hover:rotate-45 hover:scale-110 transition-all duration-300 cursor-pointer">
              <Compass className="w-6 h-6 animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: The Science of Harmony */}
      <section className="py-24 px-6 md:px-12 bg-[#F9F6F0]/80 relative overflow-hidden border-y border-[#EBE6DD]">
        {/* Ambient blob */}
        <div className="bg-blob blob-bronze top-[20%] left-[50%] -translate-x-1/2 w-[600px] h-[300px]" />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-vedic text-[#352520] mb-4">
              The Science of Harmony
            </h2>
            <p className="text-sm md:text-base text-[#5D4E37] font-light leading-relaxed">
              Our proprietary AI understands the deep energetic currents of Vastu Shastra, translating ancient geometry into actionable insights for modern floor plans.
            </p>
          </div>
          
          {/* 3 Step Milestones (Glassmorphic) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center p-6 glass-card border border-[#EBE6DD]/60 rounded-2xl">
              <div className="w-12 h-12 rounded-full bg-[#FDE68A]/30 flex items-center justify-center text-[#826239] mb-5">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold font-vedic text-[#352520] mb-3">Upload Plan</h3>
              <p className="text-xs text-[#5D4E37] leading-relaxed font-light">
                Easily upload your floor plan or layout. Our system accepts various formats and instantly begins parsing the spatial geometry.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="flex flex-col items-center text-center p-6 glass-card border border-[#EBE6DD]/60 rounded-2xl">
              <div className="w-12 h-12 rounded-full bg-[#D97706]/10 flex items-center justify-center text-[#D97706] mb-5">
                <Compass className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold font-vedic text-[#352520] mb-3">AI Analysis</h3>
              <p className="text-xs text-[#5D4E37] leading-relaxed font-light">
                The intelligence engine evaluates direction alignments, element placement, and energetic flow based on classical Vastu principles.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="flex flex-col items-center text-center p-6 glass-card border border-[#EBE6DD]/60 rounded-2xl">
              <div className="w-12 h-12 rounded-full bg-[#826239]/10 flex items-center justify-center text-[#826239] mb-5">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold font-vedic text-[#352520] mb-3">Optimize & Align</h3>
              <p className="text-xs text-[#5D4E37] leading-relaxed font-light">
                Receive a comprehensive report with specific, actionable remedies and layout adjustments to maximize positive energy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Everything Your Space Deserves (Capabilities) */}
      <section className="py-24 px-6 md:px-12 bg-white/40 relative overflow-hidden">
        {/* Background drifting blobs */}
        <div className="bg-blob blob-gold top-[25%] right-[-5%] w-[500px] h-[500px]" />
        <div className="bg-blob blob-saffron bottom-[10%] left-[-5%] w-[550px] h-[550px]" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Asymmetric Section Header */}
          <div className="text-left mb-12">
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#D97706] mb-2 block">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-vedic text-[#352520] leading-tight">
              Everything Your Space
              <br />
              <span className="text-[#D97706] italic font-normal">Deserves</span>
            </h2>
          </div>
                   {/* Capabilities Glassmorphic Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Card 01 - AI Floor Plan Analysis (Spans 2 columns) */}
            <div className="col-span-1 lg:col-span-2 p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  01
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  AI Floor Plan Analysis
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light max-w-xl">
                  Deep learning models analyze the floor plan layout — PDF, JPEG, or CAD files — detecting walls, rooms, openings, and orientations with 95%+ accuracy.
                </p>
              </div>
              
              {/* Rooms detected Tags */}
              <div className="flex flex-wrap gap-2 mt-8 pt-4 border-t border-[#EBE6DD]/40">
                <span className="bg-[#F9F6F0] text-[#826239] px-3.5 py-1.5 rounded-full text-xs font-medium border border-[#EBE6DD]">Living</span>
                <span className="bg-[#E8F5E9] text-[#2E7D32] px-3.5 py-1.5 rounded-full text-xs font-medium">Bed</span>
                <span className="bg-[#FFEBEE] text-[#C62828] px-3.5 py-1.5 rounded-full text-xs font-medium">Toilet</span>
                <span className="bg-[#FFFDE7] text-[#D97706] px-3.5 py-1.5 rounded-full text-xs font-medium">Kitchen</span>
                <span className="border border-[#EBE6DD] text-[#826239] px-3.5 py-1.5 rounded-full text-xs font-medium">8 Rooms detected</span>
              </div>
            </div>

            {/* Card 02 - Compass Mapping */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  02
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  Compass Mapping
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  Precise alignment analysis using device sensors and visual orientation for accurate directional components.
                </p>
              </div>
            </div>

            {/* Card 03 - Furniture Guidance */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  03
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  Furniture Guidance
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  AI recommends optimal furniture placement based on energy flow and directional vastu principles.
                </p>
              </div>
            </div>

            {/* Card 04 - AI Vastu Reports */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  04
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  AI Vastu Reports
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  Comprehensive PDF reports with scores, recommendations, ancient texts cites, and actionable remedies — generated in seconds.
                </p>
              </div>
            </div>

            {/* Card 05 - Camera Live Scan */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  05
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  Camera Live Scan
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  Point your phone at any space for instant AR-powered Vastu analysis overlaid in real-time.
                </p>
              </div>
            </div>

            {/* Card 06 - AI Chat Assistant */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  06
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  AI Chat Assistant
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  Ask anything about your space. The AI is trained on 5000+ years of Vedic literature to answer instantly.
                </p>
              </div>
              
              {/* Energy benefits list */}
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 mt-6 pt-4 border-t border-[#EBE6DD]/40 text-xs font-medium">
                <div className="flex items-center gap-1.5 text-[#2E7D32]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32]" />
                  Prosperity flow
                </div>
                <div className="flex items-center gap-1.5 text-[#826239]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#826239]" />
                  Family harmony
                </div>
                <div className="flex items-center gap-1.5 text-[#826239]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#826239]" />
                  Career growth
                </div>
                <div className="flex items-center gap-1.5 text-[#2E7D32]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32]" />
                  Mental peace
                </div>
              </div>
            </div>

            {/* Card 07 - Energy Heatmaps */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between group">
              <div>
                <span className="text-xs font-bold tracking-widest text-[#D97706]/70 uppercase block mb-4 group-hover:text-[#D97706] transition-colors">
                  07
                </span>
                <h3 className="text-xl font-bold font-vedic text-[#352520] mb-3">
                  Energy Heatmaps
                </h3>
                <p className="text-sm text-[#5D4E37] leading-relaxed font-light">
                  Visualized colors overlays show energy distribution across every square foot of your space.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Harmony for Every Space */}
      <section className="py-24 px-6 md:px-12 bg-[#F9F6F0]/80 relative overflow-hidden border-y border-[#EBE6DD]">
        {/* Soft background glow */}
        <div className="bg-blob blob-bronze bottom-[-10%] right-[10%] w-[450px] h-[450px]" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-vedic text-[#352520] mb-4">
              Harmony for Every Space
            </h2>
            <p className="text-sm md:text-base text-[#5D4E37] font-light leading-relaxed">
              Whether it's your home, your office, or a commercial establishment, our AI adapts to the unique energetic requirements of your environment.
            </p>
          </div>
          
          {/* 3 Space cover cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Cover 1 - Residential */}
            <div className="relative group rounded-2xl overflow-hidden aspect-[4/5] border border-[#EBE6DD]/80 shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <img
                src="/residential.jpg"
                alt="Warm luxurious living room"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              {/* Dark bottom gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-left">
                <h3 className="text-xl font-bold font-vedic mb-1">Residential</h3>
                <p className="text-xs text-white/80 font-light leading-relaxed">
                  Optimize space and prosperity of your sanctuary
                </p>
              </div>
            </div>

            {/* Cover 2 - Workspaces */}
            <div className="relative group rounded-2xl overflow-hidden aspect-[4/5] border border-[#EBE6DD]/80 shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <img
                src="/workspace.jpg"
                alt="Modern workspace home office desk"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-left">
                <h3 className="text-xl font-bold font-vedic mb-1">Workspaces</h3>
                <p className="text-xs text-white/80 font-light leading-relaxed">
                  Enhance focus, creativity, and team synergy
                </p>
              </div>
            </div>

            {/* Cover 3 - Commercial */}
            <div className="relative group rounded-2xl overflow-hidden aspect-[4/5] border border-[#EBE6DD]/80 shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <img
                src="/commercial.jpg"
                alt="High-end retail shop boutique cosmetic display"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-left">
                <h3 className="text-xl font-bold font-vedic mb-1">Commercial</h3>
                <p className="text-xs text-white/80 font-light leading-relaxed">
                  Attract business and positive customer energy
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Lived Experiences */}
      <section className="py-24 px-6 md:px-12 bg-white/40 relative overflow-hidden">
        {/* Shifting blobs */}
        <div className="bg-blob blob-gold top-[10%] left-[5%] w-[450px] h-[450px]" />
        <div className="bg-blob blob-saffron bottom-[10%] right-[5%] w-[400px] h-[400px]" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-vedic text-[#352520] mb-4">
              Lived Experiences
            </h2>
            <p className="text-sm md:text-base text-[#5D4E37] font-light leading-relaxed">
              Discover how aligning space with ancient wisdom has transformed the lives and environments of our clients.
            </p>
          </div>
          
          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between text-left">
              <div>
                <span className="text-5xl font-serif text-[#D97706]/20 block -mb-4 font-black">“</span>
                <p className="text-sm text-[#5D4E37] italic leading-relaxed font-light mb-6 relative z-10">
                  After applying the AI's recommendations to our new apartment, the shift in energy was palpable. The layout changes were simple yet feel so purposeful. Truly remarkable.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center font-bold text-xs text-[#826239] border border-[#826239]/30">
                  AS
                </div>
                <div>
                  <h4 className="text-sm font-bold font-vedic text-[#352520]">Aditi Sharma</h4>
                  <p className="text-[10px] text-[#826239] font-medium tracking-wide uppercase">Homeowner, Mumbai</p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between text-left">
              <div>
                <span className="text-5xl font-serif text-[#D97706]/20 block -mb-4 font-black">“</span>
                <p className="text-sm text-[#5D4E37] italic leading-relaxed font-light mb-6 relative z-10">
                  I was skeptical, but the AI Workspace analysis totally changed our office layout. Productivity has climbed, and the team feels much more active and positive.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center font-bold text-xs text-[#826239] border border-[#826239]/30">
                  VM
                </div>
                <div>
                  <h4 className="text-sm font-bold font-vedic text-[#352520]">Vikram Mehta</h4>
                  <p className="text-[10px] text-[#826239] font-medium tracking-wide uppercase">Tech Founder</p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="p-8 glass-card border border-[#EBE6DD]/60 rounded-2xl flex flex-col justify-between text-left">
              <div>
                <span className="text-5xl font-serif text-[#D97706]/20 block -mb-4 font-black">“</span>
                <p className="text-sm text-[#5D4E37] italic leading-relaxed font-light mb-6 relative z-10">
                  The detailed report was easy to follow. We made simple adjustments in our northeast and living area, and our home finally feels like the sanctuary we always wanted.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center font-bold text-xs text-[#826239] border border-[#826239]/30">
                  SN
                </div>
                <div>
                  <h4 className="text-sm font-bold font-vedic text-[#352520]">Sarah & Neil</h4>
                  <p className="text-[10px] text-[#826239] font-medium tracking-wide uppercase">Homeowners</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Section 6: Begin Your Journey to Balance */}
      <section className="py-24 px-6 md:px-12 bg-white/20 relative overflow-hidden">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="bg-[#FFF8F2] border-2 border-[#D97706] rounded-3xl p-10 md:p-14 text-left shadow-lg relative overflow-hidden">
            {/* Subtle background overlay */}
            <div className="absolute inset-0 bg-radial-gradient from-[#D97706]/10 to-transparent opacity-10 pointer-events-none" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative z-10">
              {/* Left Column: Text & CTA */}
              <div className="flex flex-col items-start max-w-xl">
                <h2 className="text-3xl md:text-4xl font-bold font-vedic leading-tight mb-4 text-[#352520]">
                  Begin Your Journey to Balance
                </h2>
                <p className="text-sm md:text-base text-[#5D4E37] leading-relaxed font-light mb-8">
                  Join thousands who have already transformed their environments. Upload your floor plan today and let ancient wisdom guide your modern life.
                </p>
                <Link
                  to="/upload"
                  className="group inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#C25E03] text-white font-bold text-sm px-10 py-4 rounded-full transition-all hover:scale-105 active:scale-98 duration-300 shadow-md"
                >
                  Get Free First Analysis
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              {/* Right Column: Interactive Scan Animation */}
              <div className="relative border border-[#EBE6DD] bg-white/60 backdrop-blur-sm p-5 rounded-2xl overflow-hidden shadow-inner flex items-center justify-center h-48">
                <style>{`
                  @keyframes scan {
                    0% { transform: translateY(0); opacity: 0.2; }
                    15% { opacity: 1; }
                    85% { opacity: 1; }
                    100% { transform: translateY(130px); opacity: 0.2; }
                  }
                  .scan-line {
                    animation: scan 3s linear infinite;
                  }
                `}</style>
                
                {/* SVG Blueprint */}
                <svg className="w-full h-full text-[#826239]/20" viewBox="0 0 300 200" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {/* Outer Walls */}
                  <rect x="10" y="10" width="280" height="180" rx="8" strokeWidth="2.5" />
                  
                  {/* Inner Room partitions */}
                  <line x1="100" y1="10" x2="100" y2="130" />
                  <line x1="100" y1="130" x2="160" y2="130" />
                  <line x1="200" y1="10" x2="200" y2="90" />
                  <line x1="200" y1="90" x2="290" y2="90" />
                  <line x1="10" y1="110" x2="100" y2="110" />
                  <line x1="100" y1="150" x2="290" y2="150" />
                  
                  {/* Decorative Door swings */}
                  <path d="M 100,70 A 30,30 0 0,1 70,100" strokeDasharray="3,3" />
                  <line x1="100" y1="70" x2="70" y2="70" />
                  <path d="M 200,50 A 20,20 0 0,0 180,30" strokeDasharray="3,3" />
                  <line x1="200" y1="50" x2="200" y2="30" />

                  {/* Bed representation in room */}
                  <rect x="25" y="25" width="40" height="45" rx="3" strokeDasharray="2,2" />
                  <rect x="30" y="30" width="15" height="10" rx="1" />
                  <rect x="48" y="30" width="15" height="10" rx="1" />

                  {/* Living table representation */}
                  <circle cx="150" cy="70" r="18" strokeDasharray="2,2" />
                  <circle cx="150" cy="45" r="4" />
                  <circle cx="150" cy="95" r="4" />
                  <circle cx="125" cy="70" r="4" />
                  <circle cx="175" cy="70" r="4" />

                  {/* Plants or yantra icons in zones */}
                  <circle cx="250" cy="120" r="10" strokeDasharray="2,2" />
                  <path d="M 250,113 L 250,127 M 243,120 L 257,120" />
                </svg>
                
                {/* Scanning Light Line */}
                <div className="absolute left-5 right-5 h-0.5 bg-gradient-to-r from-transparent via-[#D97706] to-transparent shadow-[0_0_10px_#D97706] scan-line" style={{ top: '20px' }} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
